/**
 * Player Dashboard Page
 * Main view for logged-in players to see their teams, events, and intel
 * 🏴‍☠️ CLASSIFIED INTEL • PECK PIRATES EYES ONLY 🏴‍☠️
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { collection, getDocs, query, orderBy, addDoc, serverTimestamp, deleteDoc, doc, where, getDoc, limit } from 'firebase/firestore';
import type { PlayerRosterDocument, EventDocument, TeamCommentDocument, EventVodDocument } from '@schemas/firestore';
import ScoutingReportModal from '../components/ScoutingReportModal';
import IntelSearch from '../components/IntelSearch';
import VodUploadModal from '../components/VodUploadModal';
import LadderChallengeModal from '../components/LadderChallengeModal';
import MyChallengesSection, { type Challenge } from '../components/MyChallengesSection';
import type { LadderEntry, LadderSettings } from '../types';

// Nudge Interface
interface ScoutingNudge {
    matchId: string;
    opponentName: string;
    round: string;
    game: string;
    eventName: string;
    eventId: string;
    eventDate: string;
}

// Nudge Interface
interface ScoutingNudge {
    matchId: string;
    opponentName: string;
    round: string;
    game: string;
    eventName: string;
    eventId: string;
    eventDate: string;
}

// Types for personal records (from start.gg import)
interface TournamentResult {
    id: string;
    tournamentName: string;
    eventName: string;
    date: string;
    location?: string;
    game: string;
    placement: number;
    placementLabel: string;
    teamName: string;
    gamerTags: string[];
    isPeckTeam: boolean;
}

interface MatchResult {
    id: string;
    date: string;
    game: string;
    season: string;
    matchType: string;
    round: string;
    peckTeamName: string;
    opponentTeamName: string;
    peckWon: boolean;
    peckScore: number;
    opponentScore: number;
    displayScore: string;
}

// Game definitions with display info
const GAMES = [
    { id: 'smashSolo', name: 'Smash Solo', icon: '🎮', color: 'bg-red-500' },
    { id: 'smashCrews', name: 'Smash Crews', icon: '👥', color: 'bg-red-600' },
    { id: 'smashJVCrews', name: 'Smash JV Crews', icon: '🛡️', color: 'bg-amber-500' },
    { id: 'rocketLeague', name: 'Rocket League', icon: '🚗', color: 'bg-blue-500' },
    { id: 'marioKart', name: 'Mario Kart', icon: '🏎️', color: 'bg-green-500' },
    { id: 'madden', name: 'Madden', icon: '🏈', color: 'bg-orange-500' },
    { id: 'fortnite', name: 'Fortnite', icon: '🎯', color: 'bg-purple-500' },
] as const;

interface EventWithMeta extends EventDocument {
    id: string;
    isPast: boolean;
    comments: TeamCommentDocument[];
    vods: EventVodDocument[];
}

export default function PlayerDashboardPage() {
    const { user, userDoc } = useAuth();
    const [playerData, setPlayerData] = useState<PlayerRosterDocument | null>(null);
    const [events, setEvents] = useState<EventWithMeta[]>([]);
    const [loading, setLoading] = useState(true);
    const [expandedEvent, setExpandedEvent] = useState<string | null>(null);
    const [newComment, setNewComment] = useState('');
    const [postingComment, setPostingComment] = useState(false);
    const [showPastEvents, setShowPastEvents] = useState(false);

    // Personal records
    const [tournamentResults, setTournamentResults] = useState<TournamentResult[]>([]);
    const [matchResults, setMatchResults] = useState<MatchResult[]>([]);
    const [showRecords, setShowRecords] = useState(false);

    // Scouting modals
    const [showScoutingModal, setShowScoutingModal] = useState(false);
    const [showIntelSearch, setShowIntelSearch] = useState(false);
    const [showVodModal, setShowVodModal] = useState(false);
    const [showChallengeModal, setShowChallengeModal] = useState(false);
    const [scoutingEventContext, setScoutingEventContext] = useState<{
        eventId?: string;
        eventDate?: string;
        eventTitle?: string;
    }>({});

    // Ladder data
    const [myLadderRank, setMyLadderRank] = useState<LadderEntry | null>(null);
    const [ladderSettings, setLadderSettings] = useState<LadderSettings | null>(null);
    const [totalLadderPlayers, setTotalLadderPlayers] = useState(0);
    const [myChallenges, setMyChallenges] = useState<Challenge[]>([]);
    const [ladderRankings, setLadderRankings] = useState<LadderEntry[]>([]);

    // Scouting Nudges
    const [scoutingNudges, setScoutingNudges] = useState<ScoutingNudge[]>([]);

    const myGames = playerData ? GAMES.filter(g =>
        playerData.games?.[g.id as keyof typeof playerData.games]
    ) : [];

    // Load player data and events
    useEffect(() => {
        async function loadData() {
            if (!user) return;

            try {
                // Find player by linked user ID or Discord ID
                const rosterSnap = await getDocs(collection(db, 'playerRoster'));
                const players = rosterSnap.docs.map(d => ({ id: d.id, ...d.data() } as PlayerRosterDocument & { id: string }));

                // Try to find matching player by linkedUserId or parentEmail (no fallback for users with no roster entry like coach)
                const myPlayer = players.find(p =>
                    p.linkedUserId === user.uid ||
                    (userDoc?.email && p.parentEmail?.toLowerCase() === userDoc.email.toLowerCase())
                );

                setPlayerData(myPlayer || null);

                // Load events
                const today = new Date().toISOString().slice(0, 10);
                const eventsSnap = await getDocs(query(collection(db, 'events'), orderBy('date', 'desc')));
                const eventsData = eventsSnap.docs.map(d => ({
                    id: d.id,
                    ...d.data(),
                    isPast: (d.data() as EventDocument).date < today,
                    comments: [] as TeamCommentDocument[],
                    vods: [] as EventVodDocument[],
                } as EventWithMeta));

                // Load comments and VoDs for each event
                const commentsSnap = await getDocs(collection(db, 'teamComments'));
                const vodsSnap = await getDocs(collection(db, 'eventVods'));

                const commentsByEvent = new Map<string, TeamCommentDocument[]>();
                commentsSnap.docs.forEach(d => {
                    const data = d.data() as TeamCommentDocument;
                    const existing = commentsByEvent.get(data.eventId) || [];
                    existing.push({ ...data, id: d.id });
                    commentsByEvent.set(data.eventId, existing);
                });

                const vodsByEvent = new Map<string, EventVodDocument[]>();
                vodsSnap.docs.forEach(d => {
                    const data = d.data() as EventVodDocument;
                    if (!data.eventId) return; // Skip VoDs without eventId
                    const existing = vodsByEvent.get(data.eventId) || [];
                    existing.push({ ...data, id: d.id });
                    vodsByEvent.set(data.eventId, existing);
                });

                eventsData.forEach(event => {
                    event.comments = commentsByEvent.get(event.id) || [];
                    event.vods = vodsByEvent.get(event.id) || [];
                });

                setEvents(eventsData);

                // Load personal records based on player's gamer tag
                if (myPlayer?.gamerTag) {
                    const gamerTag = myPlayer.gamerTag;
                    const gamerTagLower = gamerTag.toLowerCase();

                    // Fetch tournament results where this player competed
                    const tournamentsSnap = await getDocs(
                        query(collection(db, 'tournamentResults'), where('isPeckTeam', '==', true))
                    );
                    const tournaments = tournamentsSnap.docs
                        .map(d => ({ id: d.id, ...d.data() } as TournamentResult))
                        .filter(t =>
                            t.gamerTags?.some(tag => tag.toLowerCase() === gamerTagLower) ||
                            t.teamName?.toLowerCase().includes('peck')
                        )
                        .sort((a, b) => b.date.localeCompare(a.date));
                    setTournamentResults(tournaments);

                    // Fetch match results for this player's teams
                    const matchesSnap = await getDocs(
                        query(collection(db, 'matchResults'), orderBy('date', 'desc'))
                    );
                    const matches = matchesSnap.docs
                        .map(d => ({ id: d.id, ...d.data() } as MatchResult))
                        .filter(m =>
                            m.peckTeamName?.toLowerCase().includes('peck') ||
                            m.peckTeamName?.toLowerCase() === gamerTagLower
                        )
                        .slice(0, 20); // Limit to 20 most recent
                    setMatchResults(matches);
                }

                if (myPlayer) {
                    // --- CHECK FOR SCOUTING NUDGES ---
                    try {
                        const activeEventIds = eventsData
                            .filter(e => !e.isPast)
                            .map(e => e.id)
                            .slice(0, 5); // Limit to top 5 upcoming events

                        if (activeEventIds.length > 0) {
                            // Find matches where I am player1 or player2
                            const q1 = query(
                                collection(db, 'tournamentMatches'),
                                where('player1Id', '==', myPlayer.id),
                                where('status', 'in', ['pending', 'open', 'in_progress'])
                            );
                            const q2 = query(
                                collection(db, 'tournamentMatches'),
                                where('player2Id', '==', myPlayer.id),
                                where('status', 'in', ['pending', 'open', 'in_progress'])
                            );

                            const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);

                            // Combine and deduplicate
                            const uniqueMatches = new Map();
                            snap1.docs.forEach(d => uniqueMatches.set(d.id, d));
                            snap2.docs.forEach(d => uniqueMatches.set(d.id, d));

                            const nudges: ScoutingNudge[] = [];
                            const reportsRef = collection(db, 'scoutingReports');

                            for (const d of uniqueMatches.values()) {
                                const m = d.data();

                                const opponentName = m.player1Id === myPlayer.id ? m.player2Name : m.player1Name;
                                if (!opponentName || opponentName === 'TBD' || opponentName === 'Bye') continue;

                                // Check for valid report (created by anyone)
                                const reportQ = query(
                                    reportsRef,
                                    where('opponentName', '==', opponentName),
                                    limit(1)
                                );
                                const reportSnap = await getDocs(reportQ);

                                if (reportSnap.empty) {
                                    const ev = eventsData.find(e => e.id === m.eventId);
                                    if (ev) {
                                        nudges.push({
                                            matchId: d.id,
                                            opponentName,
                                            round: m.fullRoundText,
                                            game: 'smash-bros-ultimate',
                                            eventName: ev.title,
                                            eventId: m.eventId,
                                            eventDate: ev.date
                                        });
                                    }
                                }
                            }
                            setScoutingNudges(nudges);
                        }
                    } catch (err) {
                        console.error("Error fetching scouting nudges:", err);
                    }
                } // End if (myPlayer)

                // Load ladder data if player is a Smash player OR user has a direct ladder entry
                const ladderSnap = await getDocs(query(collection(db, 'ssbuLadder'), orderBy('rank', 'asc')));
                const ladderData = ladderSnap.docs.map(d => ({ id: d.id, ...d.data() })) as LadderEntry[];

                // Find ladder entry - by roster ID, linkedUserId, or user.uid as playerRosterId (for non-roster participants like coach)
                const myRank = ladderData.find(e =>
                    (myPlayer && e.playerRosterId === myPlayer.id) ||
                    e.linkedUserId === user.uid ||
                    e.playerRosterId === user.uid
                );

                // Show ladder section if user is a Smash player OR has a ladder entry
                const showLadder = myRank || (myPlayer && (myPlayer.games.smashSolo || myPlayer.games.smashCrews || myPlayer.games.smashJVCrews));

                if (showLadder) {
                    setTotalLadderPlayers(ladderData.length);
                    setLadderRankings(ladderData);
                    setMyLadderRank(myRank || null);

                    // Load ladder settings
                    const settingsDoc = await getDoc(doc(db, 'settings', 'ssbuLadder'));
                    if (settingsDoc.exists()) {
                        setLadderSettings(settingsDoc.data() as LadderSettings);
                    }

                    // Load player's challenges - match by roster ID or user ID
                    const myLadderId = myRank?.playerRosterId || myPlayer?.id || user.uid;
                    const challengesSnap = await getDocs(
                        query(
                            collection(db, 'ssbuChallenges'),
                            where('status', 'in', ['pending', 'accepted', 'awaiting_confirm', 'scheduled']),
                            orderBy('createdAt', 'desc')
                        )
                    );
                    const challengesData = challengesSnap.docs
                        .map(d => ({ id: d.id, ...d.data() }) as Challenge)
                        .filter(c => c.challengerId === myLadderId || c.defenderId === myLadderId);
                    setMyChallenges(challengesData);
                }
            } catch (err) {
                console.error('Error loading player data:', err);
            } finally {
                setLoading(false);
            }
        }
        loadData();
    }, [user, userDoc]);

    // Post a comment
    const handlePostComment = async (eventId: string, game: string) => {
        if (!newComment.trim() || !playerData || !user) return;

        setPostingComment(true);
        try {
            const isCaptain = playerData.isCaptain?.[game as keyof typeof playerData.isCaptain] || false;

            await addDoc(collection(db, 'teamComments'), {
                eventId,
                authorId: user.uid,
                authorName: `${playerData.firstName} ${playerData.lastName}`,
                isCaptain,
                game,
                content: newComment.trim(),
                createdAt: serverTimestamp(),
            });

            // Refresh comments
            const newCommentObj: TeamCommentDocument = {
                id: 'temp-' + Date.now(),
                eventId,
                authorId: user.uid,
                authorName: `${playerData.firstName} ${playerData.lastName}`,
                isCaptain,
                game,
                content: newComment.trim(),
                createdAt: new Date() as any,
            };

            setEvents(prev => prev.map(e =>
                e.id === eventId
                    ? { ...e, comments: [...e.comments, newCommentObj] }
                    : e
            ));
            setNewComment('');
        } catch (err) {
            console.error('Error posting comment:', err);
        } finally {
            setPostingComment(false);
        }
    };

    // Delete own comment
    const handleDeleteComment = async (commentId: string, eventId: string) => {
        try {
            await deleteDoc(doc(db, 'teamComments', commentId));
            setEvents(prev => prev.map(e =>
                e.id === eventId
                    ? { ...e, comments: e.comments.filter(c => c.id !== commentId) }
                    : e
            ));
        } catch (err) {
            console.error('Error deleting comment:', err);
        }
    };

    const upcomingEvents = events
        .filter(e => !e.isPast)
        .sort((a, b) => a.date.localeCompare(b.date));

    // Past events sorted closest-to-now first (descending)
    const pastEvents = events
        .filter(e => e.isPast)
        // Already sorted desc by query, but explicit sort is safer locally
        .sort((a, b) => b.date.localeCompare(a.date));

    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6 lg:p-8 max-w-5xl">
            {/* 🔒 CLASSIFIED Banner */}
            <div className="mb-6 p-4 bg-gradient-to-r from-red-900/40 via-secondary-800 to-red-900/40 border border-red-500/30 rounded-lg animate-fade-in">
                <div className="flex items-center justify-center gap-3">
                    <span className="text-2xl">🏴‍☠️</span>
                    <div className="text-center">
                        <p className="text-red-400 font-bold tracking-widest text-sm uppercase">
                            Classified Intel • Peck Pirates Eyes Only
                        </p>
                        <p className="text-secondary-400 text-xs italic mt-1">
                            "Unauthorized access will be met with competitive destruction"
                        </p>
                    </div>
                    <span className="text-2xl">🏴‍☠️</span>
                </div>
            </div>

            {/* Welcome Header */}
            <div className="mb-6 animate-fade-in">
                <h1 className="text-3xl font-display font-bold text-white">
                    Welcome back, {userDoc?.displayName?.split(' ')[0] || playerData?.firstName || user?.displayName?.split(' ')[0] || 'Pirate'}!
                </h1>
                <p className="text-secondary-400 mt-1">
                    Here's your team intel and upcoming battles.
                </p>
            </div>

            {/* Scouting Nudges - High Priority */}
            {scoutingNudges.length > 0 && (
                <div className="mb-8 animate-pulse-slow">
                    <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                        <span>🚨</span> Scouting Required
                    </h2>
                    <div className="flex flex-col gap-3">
                        {scoutingNudges.map(nudge => (
                            <div key={nudge.matchId} className="bg-red-900/20 border border-red-500/50 rounded-lg p-4 flex items-center justify-between">
                                <div>
                                    <div className="text-white font-bold text-lg">{nudge.opponentName}</div>
                                    <div className="text-red-300 text-sm">
                                        Opponent in {nudge.eventName} • {nudge.round}
                                    </div>
                                </div>
                                <button
                                    onClick={() => {
                                        setScoutingEventContext({
                                            eventId: nudge.eventId,
                                            eventDate: nudge.eventDate,
                                            eventTitle: nudge.eventName
                                        });
                                        // TODO: Pass opponent name to pre-fill modal
                                        setShowScoutingModal(true);
                                    }}
                                    className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded font-bold shadow-lg shadow-red-900/50 transition-all transform hover:scale-105"
                                >
                                    Create Report
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Scouting Nudges - High Priority */}
            {scoutingNudges.length > 0 && (
                <div className="mb-8 animate-pulse-slow">
                    <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                        <span>🚨</span> Scouting Required
                    </h2>
                    <div className="flex flex-col gap-3">
                        {scoutingNudges.map(nudge => (
                            <div key={nudge.matchId} className="bg-red-900/20 border border-red-500/50 rounded-lg p-4 flex items-center justify-between">
                                <div>
                                    <div className="text-white font-bold text-lg">{nudge.opponentName}</div>
                                    <div className="text-red-300 text-sm">
                                        Opponent in {nudge.eventName} • {nudge.round}
                                    </div>
                                </div>
                                <button
                                    onClick={() => {
                                        setScoutingEventContext({
                                            eventId: nudge.eventId,
                                            eventDate: nudge.eventDate,
                                            eventTitle: nudge.eventName
                                        });
                                        // TODO: Pass opponent name to pre-fill modal if supported
                                        setShowScoutingModal(true);
                                    }}
                                    className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded font-bold shadow-lg shadow-red-900/50 transition-all transform hover:scale-105"
                                >
                                    Create Report
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* My Teams */}
            <div className="mb-8">
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <span>⚔️</span> My Teams
                </h2>
                {myGames.length === 0 ? (
                    <div className="card text-center py-8">
                        <p className="text-secondary-400">You're not assigned to any teams yet.</p>
                        <p className="text-secondary-500 text-sm mt-1">Contact Coach to get added to a roster.</p>
                    </div>
                ) : (
                    <div className="flex flex-wrap gap-3">
                        {myGames.map(game => {
                            const isCaptain = playerData?.isCaptain?.[game.id as keyof typeof playerData.isCaptain];
                            return (
                                <div
                                    key={game.id}
                                    className={`${game.color} px-4 py-2 rounded-lg flex items-center gap-2 text-white font-medium shadow-lg`}
                                >
                                    <span className="text-lg">{game.icon}</span>
                                    {game.name}
                                    {isCaptain && <span className="ml-1" title="Team Captain">👑</span>}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* SSBU Ladder */}
            {(myLadderRank || (playerData && (playerData.games.smashSolo || playerData.games.smashCrews || playerData.games.smashJVCrews))) && (
                <div className="mb-8">
                    <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                        <span>⚔️</span> SSBU Ladder
                        {ladderSettings?.challengeWindowOpen && (
                            <span className="ml-2 px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full animate-pulse">
                                Window Open
                            </span>
                        )}
                    </h2>

                    {myLadderRank ? (
                        <div className="card">
                            {/* Rank Display */}
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-4">
                                    <div className={`text-4xl font-bold ${myLadderRank.rank === 1 ? 'text-yellow-400' :
                                        myLadderRank.rank <= 3 ? 'text-secondary-200' :
                                            'text-white'
                                        }`}>
                                        {myLadderRank.rank === 1 ? '👑' : `#${myLadderRank.rank}`}
                                    </div>
                                    <div>
                                        <div className="text-white font-semibold">
                                            {myLadderRank.gamerTag || myLadderRank.playerName}
                                        </div>
                                        <div className="text-sm text-secondary-400">
                                            of {totalLadderPlayers} players
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowChallengeModal(true)}
                                    disabled={(!ladderSettings?.challengeWindowOpen && !(ladderSettings?.isPreseason && ladderSettings?.allowOpenChallenges)) || myLadderRank.rank === 1}
                                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    ⚔️ Issue Challenge
                                </button>
                            </div>

                            {/* Stats Row */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                                <div className="bg-secondary-800/50 rounded-lg p-2">
                                    <div className="text-xl font-bold text-white">{myLadderRank.stats.challengesWon}</div>
                                    <div className="text-xs text-secondary-400">Challenges Won</div>
                                </div>
                                <div className="bg-secondary-800/50 rounded-lg p-2">
                                    <div className="text-xl font-bold text-white">{myLadderRank.stats.defensesWon}</div>
                                    <div className="text-xs text-secondary-400">Defenses Won</div>
                                </div>
                                <div className="bg-secondary-800/50 rounded-lg p-2">
                                    <div className="text-xl font-bold text-white">#{myLadderRank.stats.bestRank}</div>
                                    <div className="text-xs text-secondary-400">Best Rank</div>
                                </div>
                                <div className="bg-secondary-800/50 rounded-lg p-2">
                                    <div className={`text-xl font-bold ${myLadderRank.stats.currentStreak > 0 ? 'text-orange-400' :
                                        myLadderRank.stats.currentStreak < 0 ? 'text-blue-400' :
                                            'text-secondary-400'
                                        }`}>
                                        {myLadderRank.stats.currentStreak > 0 ? `🔥${myLadderRank.stats.currentStreak}` :
                                            myLadderRank.stats.currentStreak < 0 ? `❄️${Math.abs(myLadderRank.stats.currentStreak)}` :
                                                '—'}
                                    </div>
                                    <div className="text-xs text-secondary-400">Streak</div>
                                </div>
                            </div>

                            {/* My Challenges (preseason self-reporting) */}
                            {myChallenges.length > 0 && myLadderRank && (
                                <div className="mt-4 pt-4 border-t border-secondary-700">
                                    <MyChallengesSection
                                        playerId={myLadderRank.playerRosterId}
                                        challenges={myChallenges}
                                        rankings={ladderRankings}
                                        settings={ladderSettings}
                                        onRefresh={() => {
                                            // Trigger re-load of data
                                            const reloadLadder = async () => {
                                                const ladderSnap = await getDocs(query(collection(db, 'ssbuLadder'), orderBy('rank', 'asc')));
                                                const ladderData = ladderSnap.docs.map(d => ({ id: d.id, ...d.data() })) as LadderEntry[];
                                                setLadderRankings(ladderData);

                                                // Find by multiple criteria (roster ID, linkedUserId, or user.uid)
                                                const refreshedRank = ladderData.find(e =>
                                                    (playerData && e.playerRosterId === playerData.id) ||
                                                    e.linkedUserId === user?.uid ||
                                                    e.playerRosterId === user?.uid
                                                );
                                                setMyLadderRank(refreshedRank || null);

                                                const myLadderId = refreshedRank?.playerRosterId || playerData?.id || user?.uid;
                                                const challengesSnap = await getDocs(
                                                    query(
                                                        collection(db, 'ssbuChallenges'),
                                                        where('status', 'in', ['pending', 'accepted', 'awaiting_confirm', 'scheduled']),
                                                        orderBy('createdAt', 'desc')
                                                    )
                                                );
                                                const challengesData = challengesSnap.docs
                                                    .map(d => ({ id: d.id, ...d.data() }) as Challenge)
                                                    .filter(c => c.challengerId === myLadderId || c.defenderId === myLadderId);
                                                setMyChallenges(challengesData);
                                            };
                                            reloadLadder();
                                        }}
                                    />
                                </div>
                            )}

                            {/* Quick Info */}
                            <div className="mt-4 pt-4 border-t border-secondary-700 text-sm text-secondary-400">
                                <p>
                                    <strong className="text-white">Challenge Range:</strong> You can challenge up to{' '}
                                    {myLadderRank.rank <= (ladderSettings?.topTierThreshold || 5) ? '1 rank' : '3 ranks'} ahead.
                                    {!ladderSettings?.challengeWindowOpen && !(ladderSettings?.isPreseason && ladderSettings?.allowOpenChallenges) && (
                                        <span className="ml-2 text-yellow-400">Window is currently closed.</span>
                                    )}
                                    {ladderSettings?.isPreseason && ladderSettings?.allowOpenChallenges && !ladderSettings?.challengeWindowOpen && (
                                        <span className="ml-2 text-green-400">⚡ Open challenges allowed (preseason)</span>
                                    )}
                                </p>
                                <p className="mt-2">
                                    <strong className="text-white">Report Results:</strong>{' '}
                                    {ladderSettings?.isPreseason ? (
                                        <>Both players can report results above. When you both agree, the ladder updates automatically!</>
                                    ) : (
                                        <>Message Coach via Discord <span className="text-purple-400">#ssbu-ladder</span> channel or in-person.</>
                                    )}
                                </p>
                            </div>

                            {/* Link to full ladder */}
                            <div className="mt-4">
                                <Link to="/ladder" className="text-red-400 hover:text-red-300 text-sm font-medium">
                                    View Full Ladder Rankings →
                                </Link>
                            </div>
                        </div>
                    ) : (
                        <div className="card text-center py-6">
                            <p className="text-secondary-400">You're not on the SSBU ladder yet.</p>
                            <p className="text-secondary-500 text-sm mt-1">Contact Coach to be added.</p>
                            <Link to="/ladder" className="text-red-400 hover:text-red-300 text-sm font-medium mt-3 inline-block">
                                View Ladder Rankings →
                            </Link>
                        </div>
                    )}
                </div>
            )}

            {/* Personal Records */}
            {(tournamentResults.length > 0 || matchResults.length > 0) && (
                <div className="mb-8">
                    <button
                        onClick={() => setShowRecords(!showRecords)}
                        className="flex items-center gap-2 text-lg font-semibold text-white mb-4 hover:text-primary-400 transition-colors"
                    >
                        <span>🏆</span> My Records
                        <span className="text-sm text-secondary-400">
                            ({tournamentResults.length} tournaments, {matchResults.length} matches)
                        </span>
                        <svg
                            className={`w-4 h-4 transition-transform ${showRecords ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>

                    {showRecords && (
                        <div className="space-y-6">
                            {/* Tournament Results */}
                            {tournamentResults.length > 0 && (
                                <div className="card">
                                    <h3 className="text-md font-semibold text-white mb-4 flex items-center gap-2">
                                        <span>🎖️</span> Tournament Placements
                                    </h3>
                                    <div className="space-y-3">
                                        {tournamentResults.map(result => (
                                            <div
                                                key={result.id}
                                                className="flex items-center justify-between p-3 bg-secondary-800/50 rounded-lg"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className={`text-2xl ${result.placement === 1 ? 'text-yellow-400' :
                                                        result.placement === 2 ? 'text-gray-300' :
                                                            result.placement === 3 ? 'text-amber-600' :
                                                                'text-secondary-400'
                                                        }`}>
                                                        {result.placement === 1 ? '🥇' :
                                                            result.placement === 2 ? '🥈' :
                                                                result.placement === 3 ? '🥉' :
                                                                    `#${result.placement}`}
                                                    </div>
                                                    <div>
                                                        <p className="text-white font-medium">{result.eventName}</p>
                                                        <p className="text-sm text-secondary-400">
                                                            {result.tournamentName} • {new Date(result.date).toLocaleDateString('en-US', { timeZone: 'UTC' })}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <p className={`font-bold ${result.placement <= 3 ? 'text-primary-400' : 'text-secondary-300'
                                                        }`}>
                                                        {result.placementLabel}
                                                    </p>
                                                    <p className="text-xs text-secondary-500">{result.game}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Match History */}
                            {matchResults.length > 0 && (
                                <div className="card">
                                    <h3 className="text-md font-semibold text-white mb-4 flex items-center gap-2">
                                        <span>⚔️</span> Recent Match History
                                    </h3>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="border-b border-secondary-700">
                                                    <th className="text-left py-2 text-secondary-400 font-medium">Date</th>
                                                    <th className="text-left py-2 text-secondary-400 font-medium">Round</th>
                                                    <th className="text-left py-2 text-secondary-400 font-medium">Opponent</th>
                                                    <th className="text-center py-2 text-secondary-400 font-medium">Score</th>
                                                    <th className="text-center py-2 text-secondary-400 font-medium">Result</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {matchResults.map(match => (
                                                    <tr key={match.id} className="border-b border-secondary-800 hover:bg-secondary-800/30">
                                                        <td className="py-2 text-secondary-300">
                                                            {new Date(match.date).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' })}
                                                        </td>
                                                        <td className="py-2 text-secondary-300">{match.round}</td>
                                                        <td className="py-2 text-white">{match.opponentTeamName}</td>
                                                        <td className="py-2 text-center text-secondary-300">
                                                            {match.peckScore} - {match.opponentScore}
                                                        </td>
                                                        <td className="py-2 text-center">
                                                            <span className={`px-2 py-0.5 rounded text-xs font-bold ${match.peckWon
                                                                ? 'bg-green-500/20 text-green-400'
                                                                : 'bg-red-500/20 text-red-400'
                                                                }`}>
                                                                {match.peckWon ? 'WIN' : 'LOSS'}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    {/* Win/Loss Summary */}
                                    <div className="mt-4 pt-4 border-t border-secondary-700 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-6">
                                        <div className="text-sm">
                                            <span className="text-secondary-400">Record: </span>
                                            <span className="text-green-400 font-bold">
                                                {matchResults.filter(m => m.peckWon).length}W
                                            </span>
                                            <span className="text-secondary-500"> - </span>
                                            <span className="text-red-400 font-bold">
                                                {matchResults.filter(m => !m.peckWon).length}L
                                            </span>
                                        </div>
                                        <div className="text-sm text-secondary-400">
                                            Win Rate: {' '}
                                            <span className="text-white font-medium">
                                                {matchResults.length > 0
                                                    ? Math.round((matchResults.filter(m => m.peckWon).length / matchResults.length) * 100)
                                                    : 0}%
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Upcoming Events */}
            <div className="mb-8">
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <span>📅</span> Upcoming Events
                </h2>
                {upcomingEvents.length === 0 ? (
                    <div className="card text-center py-8">
                        <p className="text-secondary-400">No upcoming events scheduled.</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {upcomingEvents.slice(0, 5).map(event => (
                            <EventCard
                                key={event.id}
                                event={event}
                                isExpanded={expandedEvent === event.id}
                                onToggle={() => setExpandedEvent(expandedEvent === event.id ? null : event.id)}
                                newComment={newComment}
                                setNewComment={setNewComment}
                                onPostComment={handlePostComment}
                                onDeleteComment={handleDeleteComment}
                                onAddScoutingReport={(e) => {
                                    setScoutingEventContext({ eventId: e.id, eventDate: e.date, eventTitle: e.title });
                                    setShowScoutingModal(true);
                                }}
                                postingComment={postingComment}
                                currentUserId={user?.uid || ''}
                                myGames={myGames}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Past Events (Collapsible) */}
            <div className="mb-8">
                <button
                    onClick={() => setShowPastEvents(!showPastEvents)}
                    className="flex items-center gap-2 text-lg font-semibold text-white mb-4 hover:text-primary-400 transition-colors"
                >
                    <span>📜</span> Past Events
                    <span className="text-sm text-secondary-400">({pastEvents.length})</span>
                    <svg
                        className={`w-4 h-4 transition-transform ${showPastEvents ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </button>

                {showPastEvents && (
                    <div className="space-y-4">
                        {pastEvents.slice(0, 10).map(event => (
                            <EventCard
                                key={event.id}
                                event={event}
                                isExpanded={expandedEvent === event.id}
                                onToggle={() => setExpandedEvent(expandedEvent === event.id ? null : event.id)}
                                newComment={newComment}
                                setNewComment={setNewComment}
                                onPostComment={handlePostComment}
                                onDeleteComment={handleDeleteComment}
                                onAddScoutingReport={(e) => {
                                    setScoutingEventContext({ eventId: e.id, eventDate: e.date, eventTitle: e.title });
                                    setShowScoutingModal(true);
                                }}
                                postingComment={postingComment}
                                currentUserId={user?.uid || ''}
                                myGames={myGames}
                                isPast
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Quick Actions */}
            <div className="card">
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <span>⚡</span> Quick Actions
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                        onClick={() => {
                            setScoutingEventContext({});
                            setShowScoutingModal(true);
                        }}
                        className="btn-primary text-center"
                    >
                        📝 New Scouting Report
                    </button>
                    <button
                        onClick={() => setShowIntelSearch(true)}
                        className="btn-secondary text-center"
                    >
                        🔍 Search Intel Database
                    </button>
                    <button
                        onClick={() => setShowVodModal(true)}
                        className="btn-ghost border border-purple-500/30 text-purple-400 text-center hover:bg-purple-500/10"
                    >
                        📹 Upload VoD
                    </button>
                    <Link
                        to="/schedule"
                        className="btn-ghost border border-secondary-600 text-center"
                    >
                        📅 View Full Schedule
                    </Link>
                </div>
            </div>

            {/* Scouting Report Modal */}
            <ScoutingReportModal
                isOpen={showScoutingModal}
                onClose={() => setShowScoutingModal(false)}
                eventId={scoutingEventContext.eventId}
                eventDate={scoutingEventContext.eventDate}
                eventTitle={scoutingEventContext.eventTitle}
                userId={user?.uid || ''}
                userName={playerData ? `${playerData.firstName} ${playerData.lastName}` : user?.displayName || 'Unknown'}
            />

            {/* Intel Search Modal */}
            <IntelSearch
                isOpen={showIntelSearch}
                onClose={() => setShowIntelSearch(false)}
            />

            {/* VoD Upload Modal */}
            <VodUploadModal
                isOpen={showVodModal}
                onClose={() => setShowVodModal(false)}
                userId={user?.uid || ''}
                userName={playerData ? `${playerData.firstName} ${playerData.lastName}` : user?.displayName || 'Unknown'}
            />

            {/* Ladder Challenge Modal */}
            {myLadderRank && (
                <LadderChallengeModal
                    isOpen={showChallengeModal}
                    onClose={() => setShowChallengeModal(false)}
                    playerId={myLadderRank.playerRosterId}
                    playerName={myLadderRank.gamerTag || myLadderRank.playerName}
                />
            )}
        </div>
    );
}

// Event Card Component
interface EventCardProps {
    event: EventWithMeta;
    isExpanded: boolean;
    onToggle: () => void;
    newComment: string;
    setNewComment: (v: string) => void;
    onPostComment: (eventId: string, game: string) => void;
    onDeleteComment: (commentId: string, eventId: string) => void;
    onAddScoutingReport: (event: EventWithMeta) => void;
    postingComment: boolean;
    currentUserId: string;
    myGames: typeof GAMES[number][];
    isPast?: boolean;
}

function EventCard({
    event,
    isExpanded,
    onToggle,
    newComment,
    setNewComment,
    onPostComment,
    onDeleteComment,
    onAddScoutingReport,
    postingComment,
    currentUserId,
    myGames,
    isPast,
}: EventCardProps) {
    const eventDate = new Date(event.date);
    const hasVods = event.vods.length > 0;
    const commentCount = event.comments.length;

    return (
        <div className={`card ${isPast ? 'opacity-80' : ''}`}>
            {/* Event Header */}
            <button
                onClick={onToggle}
                className="w-full flex items-center justify-between text-left"
            >
                <div className="flex items-center gap-4">
                    {/* Date */}
                    <div className="w-14 text-center">
                        <div className="text-xl font-bold text-white">{eventDate.getUTCDate()}</div>
                        <div className="text-xs text-secondary-400">
                            {eventDate.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short' })}
                        </div>
                    </div>

                    {/* Info */}
                    <div>
                        <h3 className="font-semibold text-white">{event.title}</h3>
                        <p className="text-sm text-secondary-400">
                            {event.startTime} - {event.endTime} • {event.location}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    {/* Badges */}
                    {hasVods && (
                        <span className="text-xs px-2 py-1 bg-purple-500/20 text-purple-400 rounded">
                            📹 VoD
                        </span>
                    )}
                    {commentCount > 0 && (
                        <span className="text-xs px-2 py-1 bg-blue-500/20 text-blue-400 rounded">
                            💬 {commentCount}
                        </span>
                    )}
                    {isPast && (
                        <span className="text-xs px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded">
                            📝 Intel
                        </span>
                    )}

                    <svg
                        className={`w-5 h-5 text-secondary-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </div>
            </button>

            {/* Expanded Content */}
            {isExpanded && (
                <div className="mt-4 pt-4 border-t border-secondary-700 space-y-4">
                    {/* VoDs */}
                    {hasVods && (
                        <div>
                            <h4 className="text-sm font-semibold text-secondary-300 mb-2">📹 VoDs</h4>
                            <div className="space-y-2">
                                {event.vods.map(vod => (
                                    <a
                                        key={vod.id}
                                        href={vod.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block p-2 bg-secondary-800 rounded hover:bg-secondary-700 transition-colors"
                                    >
                                        <p className="text-white text-sm">{vod.title}</p>
                                        <p className="text-xs text-secondary-400">{vod.game}</p>
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Comments */}
                    <div>
                        <h4 className="text-sm font-semibold text-secondary-300 mb-2">💬 Team Chat</h4>

                        {/* Comment List */}
                        {event.comments.length > 0 ? (
                            <div className="space-y-2 mb-3">
                                {event.comments.map(comment => (
                                    <div key={comment.id} className="p-2 bg-secondary-800/50 rounded">
                                        <div className="flex items-center justify-between mb-1">
                                            <p className="text-sm font-medium text-white">
                                                {comment.authorName}
                                                {comment.isCaptain && <span className="ml-1">👑</span>}
                                            </p>
                                            {comment.authorId === currentUserId && (
                                                <button
                                                    onClick={() => onDeleteComment(comment.id, event.id)}
                                                    className="text-xs text-red-400 hover:text-red-300"
                                                >
                                                    Delete
                                                </button>
                                            )}
                                        </div>
                                        <p className="text-sm text-secondary-300">{comment.content}</p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-secondary-500 mb-3">No comments yet. Be the first!</p>
                        )}

                        {/* New Comment Form */}
                        {myGames.length > 0 && (
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={newComment}
                                    onChange={(e) => setNewComment(e.target.value)}
                                    placeholder="Add a comment for your team..."
                                    className="flex-1 px-3 py-2 bg-secondary-800 border border-secondary-600 rounded text-white text-sm focus:outline-none focus:border-primary-500"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            onPostComment(event.id, myGames[0].id);
                                        }
                                    }}
                                />
                                <button
                                    onClick={() => onPostComment(event.id, myGames[0].id)}
                                    disabled={postingComment || !newComment.trim()}
                                    className="btn-primary text-sm px-4 disabled:opacity-50"
                                >
                                    {postingComment ? '...' : 'Post'}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Scouting Report Button (Past Events) */}
                    {isPast && (
                        <div className="pt-2">
                            <button
                                onClick={() => onAddScoutingReport(event)}
                                className="btn-ghost border border-yellow-500/30 text-yellow-400 text-sm w-full hover:bg-yellow-500/10"
                            >
                                📝 Add Scouting Report
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
